import evaluateFieldConditions from '../evaluate-field-conditions';
import { CONFIG_PROCESSORS } from './processors';

export const isRuntimeProcessorField = field => {
  const runtimeConfig = field?.runtimeConfig || field?.runtime_config;
  return field?.type === 'hidden' && !!runtimeConfig?.processor;
};

export const shouldExcludeOnSubmit = field => {
  if (!field) return false;
  const explicit = field.excludeOnSubmit ?? field.exclude_on_submit;
  if (explicit === false || explicit === 'false' || explicit === 0) return false;
  if (explicit === true || explicit === 'true' || explicit === 1) return true;
  return isRuntimeProcessorField(field);
};

export const collectExcludeOnSubmitCodes = (fields, prefix = '') => {
  const codes = [];
  if (!Array.isArray(fields)) return codes;

  for (const field of fields) {
    const code = field?.code;
    if (!code) continue;

    const path = prefix ? `${prefix}.${code}` : code;
    if (shouldExcludeOnSubmit(field)) {
      codes.push(path);
      if (!prefix) {
        codes.push(code);
      }
    }

    const subFields = field.subFields ?? field.sub_fields;
    if (Array.isArray(subFields)) {
      codes.push(...collectExcludeOnSubmitCodes(subFields, path));
    }
  }

  return [...new Set(codes)];
};

export const omitExcludedOnSubmit = (values, fields) => {
  if (!values || typeof values !== 'object' || !Array.isArray(fields)) {
    return values;
  }

  const excludeCodes = new Set(collectExcludeOnSubmitCodes(fields));
  if (excludeCodes.size === 0) return values;

  const next = { ...values };
  for (const code of excludeCodes) {
    delete next[code];
  }
  return next;
};

const getRuntimeProcessorField = (fields, code) =>
  fields.find(f => isRuntimeProcessorField(f) && f.code === code && !shouldExcludeOnSubmit(f));

const isProcessorOwnedFieldValue = (fieldCode, rawValues, fields, context) => {
  const processorField = getRuntimeProcessorField(fields, fieldCode);
  if (!processorField) return false;

  const runtimeConfig = processorField.runtimeConfig || processorField.runtime_config;
  const processorFn = CONFIG_PROCESSORS[runtimeConfig?.processor];
  if (!processorFn) return false;

  const params = runtimeConfig.processorParams || runtimeConfig.processor_params || {};
  const withValue = processorFn(rawValues, context, params, processorField);
  const withoutField = { ...rawValues };
  delete withoutField[fieldCode];
  const withoutValue = processorFn(withoutField, context, params, processorField);

  return (
    withValue !== undefined && withValue !== null && withValue !== '' && withValue === withoutValue
  );
};

const runFieldConfigProcessors = (fields, formValues, context) => {
  const updates = {};
  if (!Array.isArray(fields)) return updates;

  for (const field of fields) {
    if (!isRuntimeProcessorField(field)) continue;

    const runtimeConfig = field.runtimeConfig || field.runtime_config;
    const processorFn = CONFIG_PROCESSORS[runtimeConfig.processor];
    if (!processorFn) continue;

    const params = runtimeConfig.processorParams || runtimeConfig.processor_params || {};
    updates[field.code] = processorFn(formValues, context, params);
  }

  return updates;
};

export const clearHiddenFieldValues = (
  fields,
  getValues,
  setValue,
  formValuesPrefix = '',
  context = {},
) => {
  if (!fields || !Array.isArray(fields) || fields.length === 0) {
    return [];
  }

  const {
    company = null,
    enabledCompanyChecks,
    companyCheckConfigurations = null,
    companyCheckConfig = null,
    candidate = null,
    candidateValues = null,
    checkDetails = null,
    subCheckDetails = null,
    selectedOptionsContext = {},
  } = context;

  const rawValues = getValues(formValuesPrefix) || {};
  const templateSources = {
    company,
    enabled_company_checks: enabledCompanyChecks ?? {},
    company_check_configurations: companyCheckConfigurations ?? {},
    configuration: companyCheckConfig ?? {},
    candidate,
    candidate_values: candidateValues,
    check_details: checkDetails,
    subcheck_details: subCheckDetails,
  };

  const currentValues = {
    ...rawValues,
    company_teams: company?.teams ?? [],
    configuration: companyCheckConfig ?? {},
    ...runFieldConfigProcessors(fields, rawValues, templateSources),
  };

  const contextForEvaluation = {
    company,
    enabledCompanyChecks,
    companyCheckConfigurations,
    companyCheckConfig,
    candidate,
    candidateValues,
    checkDetails,
    subCheckDetails,
    selectedOptionsContext,
  };

  const fieldsToClear = [];
  for (const field of fields) {
    if (isRuntimeProcessorField(field)) continue;
    if (field.type === 'hidden') continue;

    let conditionsMet = true;
    if (field.conditions) {
      try {
        conditionsMet = evaluateFieldConditions(
          field.conditions,
          currentValues,
          contextForEvaluation,
        );
      } catch (error) {
        console.error('Error evaluating field conditions in clearHiddenFieldValues:', error, field);
        conditionsMet = true;
      }
    }

    if (!conditionsMet) {
      if (isProcessorOwnedFieldValue(field.code, rawValues, fields, templateSources)) {
        continue;
      }

      const fieldPath = formValuesPrefix ? `${formValuesPrefix}.${field.code}` : field.code;
      const currentValue = getValues(fieldPath);

      const hasValue =
        currentValue !== undefined &&
        currentValue !== null &&
        !(Array.isArray(currentValue) && currentValue.length === 0) &&
        !(typeof currentValue === 'object' && Object.keys(currentValue).length === 0);

      if (hasValue) {
        fieldsToClear.push(fieldPath);
      }

      const allFormValues = getValues();
      Object.keys(allFormValues || {}).forEach(key => {
        const baseKey =
          formValuesPrefix && key.startsWith(`${formValuesPrefix}.`)
            ? key.substring(formValuesPrefix.length + 1)
            : key;

        const matchesFieldPattern = baseKey === field.code || baseKey.startsWith(`${field.code}.`);

        if (matchesFieldPattern && key !== fieldPath) {
          const nestedValue = getValues(key);
          const hasNestedValue =
            nestedValue !== undefined &&
            nestedValue !== null &&
            !(Array.isArray(nestedValue) && nestedValue.length === 0) &&
            !(typeof nestedValue === 'object' && Object.keys(nestedValue).length === 0);

          if (hasNestedValue) {
            fieldsToClear.push(key);
          }
        }
      });
    }
  }

  fieldsToClear.forEach(fieldPath => {
    setValue(fieldPath, undefined, { shouldValidate: false, shouldDirty: false });
  });

  return fieldsToClear;
};
